/**
 * EducationSection Component
 * Design: "Light with Dark Accents"
 * White timeline cards on #ffffff background, colored dots and borders
 * No scroll animations – content always fully visible
 */
import { MapPin } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

const itemColors = ["blue", "emerald", "blue", "emerald"];

const colorMap = {
  blue: {
    dot: "bg-blue-500",
    dotRing: "border-blue-300 bg-blue-50",
    border: "border-blue-200",
    ring: "ring-blue-200",
    accent: "#2563EB",
    icon: "text-blue-400",
  },
  emerald: {
    dot: "bg-emerald-500",
    dotRing: "border-emerald-300 bg-emerald-50",
    border: "border-emerald-200",
    ring: "ring-emerald-200",
    accent: "#047857",
    icon: "text-emerald-600",
  },
};

export default function EducationSection() {
  const { t } = useLanguage();

  return (
    <section id="education" className="py-24 lg:py-32 relative" style={{ background: "#ffffff" }}>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-start">
          {/* Left: Header */}
          <div>
            <span className="text-blue-600 text-sm font-semibold tracking-widest uppercase mb-4 block font-sora">
              {t.education.sectionLabel}
            </span>
            <h2 className="font-sora font-black text-3xl sm:text-4xl lg:text-5xl text-slate-900 leading-tight mb-6">
              {t.education.headline1}<br />
              <span className="text-blue-600">{t.education.headline2}</span>
            </h2>
            <p className="text-slate-700 leading-relaxed font-inter mb-8">
              {t.education.subtext}
            </p>
          </div>

          {/* Right: Timeline */}
          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-5 top-6 bottom-6 w-px bg-gradient-to-b from-blue-300 via-emerald-200 to-blue-100" />

            <div className="space-y-6">
              {t.education.items.map((item, i) => {
                const colorKey = itemColors[i] as keyof typeof colorMap;
                const colors = colorMap[colorKey];
                return (
                  <div key={i} className="flex gap-6">
                    {/* Dot */}
                    <div className="flex-shrink-0 mt-1.5">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 ${colors.dotRing}`}>
                        <div className={`w-3 h-3 rounded-full ${colors.dot}`} />
                      </div>
                    </div>

                    {/* Content */}
                    <div className={`flex-1 bg-[#F8F9FC] border rounded-xl p-5 hover:shadow-md transition-shadow ${colors.border} ${i === 0 ? `ring-2 ${colors.ring}` : ""}`}>
                      <h3 className="font-sora font-bold text-slate-900 text-lg mb-1">{item.degree}</h3>
                      <p className="font-sora font-semibold text-sm mb-2" style={{ color: colors.accent }}>
                        {item.institution}
                      </p>
                      <div className="flex items-center gap-1.5 mb-3">
                        <MapPin size={12} className={colors.icon} />
                        <span className="text-slate-600 text-xs font-inter">{item.location}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
